import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { TextArea } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import { SectionTitle } from 'packages/kakapo-ui-kit/components/common/Headings';
import HorizontalRule from 'packages/kakapo-ui-kit/components/common/HorizontalRule';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import PersonAvatar, {
	PersonAvatarSize,
} from 'packages/kakapo-ui-kit/components/users/PersonAvatar';
import Select from 'packages/kakapo-ui-kit/components/forms/controls/Select';
import useFeature from 'packages/kakapo-ui-application/hooks/useFeature';

const Row = styled(Flex)`
	align-items: center;
`;

const Cell = styled(Box)<{ width?: number; head?: boolean }>`
	flex-grow: ${(props) => (props.width === undefined ? 1 : 0)};
	flex-shrink: ${(props) => (props.width === undefined ? 1 : 0)};
	padding-left: 5px;
	padding-right: 5px;
	color: ${(props) => props.theme.colors.slateBlue};

	${(props) => props.head && 'font-weight: bold;'};
`;

function TalentReviewAxis(props: {
	axis: graphql.TalentReviewTemplateAxis;
	value: number;
	onChange: (value: number) => void;
}) {
	const { localize } = useLocalize();

	const options = props.axis.ticks.map((tick, index) => ({
		label: localize(tick).text,
		value: index,
	}));

	return (
		<Select
			small
			value={options[props.value]}
			options={options}
			onChange={(option) => props.onChange(option!.value)}
			unspaced
		/>
	);
}

function useScoreOptions() {
	const { t } = useTranslation();

	return [
		{
			label: t('talentReviews.edit.motivations.score.excellent'),
			value: graphql.TalentReviewPlacementScore.Excellent,
		},
		{
			label: t('talentReviews.edit.motivations.score.good'),
			value: graphql.TalentReviewPlacementScore.Good,
		},
		{
			label: t('talentReviews.edit.motivations.score.roomForImprovement'),
			value: graphql.TalentReviewPlacementScore.RoomForImprovement,
		},
		{
			label: t('talentReviews.edit.motivations.score.insufficient'),
			value: graphql.TalentReviewPlacementScore.Insufficient,
		},
		{
			label: t('talentReviews.edit.motivations.score.cannotBeReviewed'),
			value: graphql.TalentReviewPlacementScore.CannotBeReviewed,
		},
	];
}

function TalentReviewScore(props: {
	value?: graphql.TalentReviewPlacementScore;
	onChange: (value?: graphql.TalentReviewPlacementScore) => void;
}) {
	const options = useScoreOptions();

	return (
		<Select
			small
			value={options.find(({ value }) => value === props.value)}
			options={options}
			onChange={(option) => props.onChange(option?.value)}
			unspaced
			isClearable
		/>
	);
}

function TalentReviewRow(props: {
	template: graphql.TalentReviewTemplate;
	placements: graphql.TalentReviewPlacement[];
	user: graphql.User;
	onUpdate: (placement: graphql.TalentReviewPlacement) => void;
	viewerCanUpdate?: boolean;
}) {
	const { t } = useTranslation();
	const { localize } = useLocalize();

	const placement = props.placements.find((placement) => placement.user.id === props.user.id);

	const scoreOptions = useScoreOptions();

	const hasScores = useFeature(graphql.FeatureName.TalentReviewsScores);

	if (!placement) {
		return null;
	}

	return (
		<>
			<Row py={2}>
				<Cell width={38} flexGrow={0} flexShrink={0}>
					<PersonAvatar person={props.user} size={PersonAvatarSize.Small} />
				</Cell>
				<Cell width={140} flexGrow={0} flexShrink={0}>
					{props.user.firstName} {props.user.lastName}
				</Cell>
				<Flex alignItems="center" flexGrow={1} flexShrink={1}>
					<Cell flexBasis={1}>
						{props.viewerCanUpdate ? (
							<TalentReviewAxis
								axis={props.template.xAxis}
								value={placement.column}
								onChange={(column) => props.onUpdate({ ...placement, column })}
							/>
						) : (
							localize(props.template.xAxis.ticks[placement.column]).text
						)}
					</Cell>
					<Cell flexBasis={1}>
						{props.viewerCanUpdate ? (
							<TalentReviewAxis
								axis={props.template.yAxis}
								value={placement.row}
								onChange={(row) => props.onUpdate({ ...placement, row })}
							/>
						) : (
							localize(props.template.yAxis.ticks[placement.row]).text
						)}
					</Cell>
					{hasScores && (
						<Cell flexBasis={1}>
							{props.viewerCanUpdate ? (
								<TalentReviewScore
									value={placement.score ?? undefined}
									onChange={(score) => props.onUpdate({ ...placement, score })}
								/>
							) : (
								scoreOptions.find(({ value }) => value === placement.score)?.label ?? '-'
							)}
						</Cell>
					)}
				</Flex>
			</Row>
			<Row pb={2}>
				<Cell width={38} flexGrow={0} flexShrink={0} />
				<Cell width={140} flexGrow={0} flexShrink={0} />
				<Cell flexGrow={1} flexShrink={1}>
					{props.viewerCanUpdate ? (
						<TextArea
							minRows={0}
							placeholder={t('talentReviews.edit.motivationPlaceholder')}
							value={placement.motivation ?? undefined}
							onChange={(motivation) => props.onUpdate({ ...placement, motivation })}
						/>
					) : (
						placement.motivation
					)}
				</Cell>
			</Row>
		</>
	);
}

export default function TalentReviewMotivations(props: {
	review: graphql.TalentReview;
	placements: graphql.TalentReviewPlacement[];
	onUpdate: (placement: graphql.TalentReviewPlacement) => void;
}) {
	const { localize } = useLocalize();
	const { t } = useTranslation();

	const hasScores = useFeature(graphql.FeatureName.TalentReviewsScores);

	if (props.placements.length === 0) {
		return null;
	}

	return (
		<Box>
			<Box my={2}>
				<SectionTitle>{t('talentReviews.edit.motivations.title')}</SectionTitle>
			</Box>

			<Box mb={3}>
				<HorizontalRule />
			</Box>

			<Box>
				<Row py={2}>
					<Cell width={38} />
					<Cell width={140}>{t('talentReviews.edit.motivations.name')}</Cell>
					<Flex alignItems="center" flexGrow={1} flexShrink={1}>
						<Cell head flexBasis={1}>
							{localize(props.review.template.xAxis.label).text}
						</Cell>
						<Cell head flexBasis={1}>
							{localize(props.review.template.yAxis.label).text}
						</Cell>
						{hasScores && (
							<Cell head flexBasis={1}>
								{t('talentReviews.edit.motivations.score.label')}
							</Cell>
						)}
					</Flex>
				</Row>
				{_.sortBy(
					props.review.group.members.edges,
					({ node }) => `${node.firstName} ${node.lastName}`,
				).map(({ node }) => (
					<TalentReviewRow
						key={node.id}
						template={props.review.template}
						placements={props.placements}
						user={node}
						onUpdate={props.onUpdate}
						viewerCanUpdate={props.review.viewerCanUpdate}
					/>
				))}
			</Box>
		</Box>
	);
}
